import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';

import { Icons } from '../Common';
import { Size } from '../Common/Icons';
import { DrawerContext } from './NavBar';
import { useTabletOrMobile } from '../../utils/hooks/useTabletOrMobile';
import ModelType from '../Model/ModelType';
import ModelsContainer from '../Model/ModelsContainer';
import { SignOutButton } from './SignOutButton';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { getConfig, getClientVersion } from '../../config';
import { Loading } from '../App/Loading';
import { ErrorComponent } from '../App/ErrorComponent';
import './Navbar.css';
import { Title, Flex, FlexItem, Text } from '@patternfly/react-core';
import { AuthService } from '../../services/AuthService/AuthService';
import HelpButton from '../Help/HelpButton';
import _ from 'lodash';
import { useTranslation as useBeaconTranslation } from 'localization/useTranslation';
import { useTranslation } from 'react-i18next';
import { Settings } from 'luxon';
import LicenseModal from 'components/Help/LicenseModal';

const ShowLicense = ({ hasAcceptedLicense, onAcceptLicense, isLoading }) => {
  return <LicenseModal isOpen={!isLoading && !hasAcceptedLicense} onAcceptLicense={onAcceptLicense} file={'license'} />;
};

export const UserMenu = () => {
  const authService = useContext(AuthService);
  const isTabletOrMobile = useTabletOrMobile();
  const { isDrawerOpen, setIsDrawerOpen } = useContext(DrawerContext);

  const updateUser = useCallback((userInfo) => {
    authService.updateUserData(userInfo);
  }, [authService]);

  const onAcceptLicense = useCallback(() => {
    authService.user.acceptLicense();
  }, [authService]);

  return (
    <>
      <Flex style={{ alignItems: 'center' }}>
        <FlexItem>
          <HelpButton className="light-user-icon" file={'main'} />
        </FlexItem>
        <Flex
          style={{ alignItems: 'center' }}
          onClick={() => setIsDrawerOpen(!isDrawerOpen)}
          className="cursor-pointer">
          { (!isTabletOrMobile && <FlexItem>
            <Title headingLevel="h3" className="light-user-icon">{ authService.user?.fullName }</Title>
          </FlexItem>) }
          <FlexItem>
            <Icons.User size={Size.LARGE} className="light-user-icon"/>
          </FlexItem>
        </Flex>
      </Flex>

      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={{ scopes: getConfig().msalConfig.auth.clientId }}
        errorComponent={ErrorComponent}
        loadingComponent={Loading}
      >
        <UserDrawer
          info={authService.user}
          setInfo={updateUser}
          isDrawerOpen={isDrawerOpen}
        />
        <ShowLicense
          isLoading={authService.user.isLoading}
          hasAcceptedLicense={authService.user.hasAcceptedLicense}
          onAcceptLicense={onAcceptLicense}
        />
      </MsalAuthenticationTemplate>
    </>
  );
};


const UserDrawer = ({ info, isDrawerOpen, setInfo }) => {
  const isTabletOrMobile = useTabletOrMobile();
  const { i18n } = useTranslation();
  const T = useBeaconTranslation();

  const setData = useCallback(({ currentOrgs, settings }) => {
    i18n.changeLanguage(settings.language);
    Settings.defaultLocale = settings.language;
    setInfo({ ...info, currentOrgs, settings });
  }, [i18n, info, setInfo]);

  const model = useMemo(() => {
    const localized = getConfig()?.userMenu?.localized ?? true;
    const devOptions = [
      !localized && {
        type: ModelType.OPTIONSV2,
        label: T.common.language,
        path: 'settings.language',
        options: _.map({ en: 'English', sv: 'Svenska' }, (text, value) => ({ text, value }))
      },
      !localized && {
        type: ModelType.OPTIONSV2,
        label: 'Units',
        path: 'settings.units',
        options: _.map({ metric: 'Metric', imperial: 'Imperial' }, (text, value) => ({ text, value }))
      },
      {
        type: ModelType.CLIPBOARD_COPY,
        label: T.common.userId,
        disabled: true,
        isRequired: false,
        path: 'userId'
      },
      {
        type: ModelType.CLIPBOARD_COPY,
        label: T.common.apiToken,
        disabled: true,
        isRequired: false,
        path: 'apiToken'
      }
    ];

    const menu = [
      {
        type: ModelType.OPTIONSV2,
        label: T.common.organization,
        path: 'currentOrgs',
        options: _.map(info?.organizations, (org) => ({ text: org.displayName, value: org })),
        properties: {
          isMultiple: true,
          isTypeahead: false,
          isClearable: true,
          iteratee: _.iteratee('id')
        }
      },
      localized && {
        type: ModelType.OPTIONSV2,
        label: T.common.language,
        path: 'settings.language',
        options: _.map({ en: 'English', sv: 'Svenska' }, (text, value) => ({ text, value }))
      },
      localized && {
        type: ModelType.OPTIONSV2,
        label: 'Units',
        path: 'settings.units',
        options: _.map({ metric: 'Metric', imperial: 'Imperial' }, (text, value) => ({ text, value }))
      },
      {
        type: ModelType.CLIPBOARD_COPY,
        label: T.common.loginId,
        disabled: true,
        isRequired: false,
        path: 'userName'
      }
    ];
    return info?.settings?.claims?.isDeveloper ? _.compact([...menu, ...devOptions]) : _.compact(menu);
  }, [T.common.apiToken, T.common.language, T.common.loginId, T.common.organization, T.common.userId, info?.organizations, info?.settings?.claims?.isDeveloper]);

  return (
    <div
      className={classNames('user-drawer-form',
        {
          'user-drawer-tablet-width': isTabletOrMobile,
          'user-drawer-desktop-width': !isTabletOrMobile,
          'user-drawer-open': isDrawerOpen,
          'user-drawer-closed': !isDrawerOpen
        })}
    >
      <div
        className="user-drawer-item"
      >
        <ModelsContainer models={model} data={info} setData={setData}/>
        <SignOutButton />
        <br/>
        <Text component="small">{ getClientVersion() }</Text>
      </div>
    </div>
  );
};
